import { useTranslation } from 'react-i18next';
import React from 'react';
import { Fotter, Header } from "../components/layout";
import { aboutUsImage } from '../assets';
import { motion } from 'framer-motion';
import { 
  FaClock, 
  FaUsers, 
  FaAward,
  FaHeart,
  FaFire,
  FaLeaf,
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaQuoteLeft,
  FaCheckCircle,
  FaFish
} from 'react-icons/fa';
import { PiChefHatLight, PiCowFill } from "react-icons/pi";
import { MdOutlineRestaurant, MdDeliveryDining } from 'react-icons/md';
import { GiHotSpices, GiChickenOven } from 'react-icons/gi';
import { FaFlag } from "react-icons/fa";

function AboutUs() {
  const { t } = useTranslation();

  // Hero Section Data
  const heroData = {
    icon: FaFlag,
    title: "About Us",
    description: "We bring you the most delicious authentic Yemeni cuisine in the heart of Egypt, where traditional flavors meet modern touches.",
    iconColor: "text-color-1",
    bgGradient: "from-color-4/95 to-color-2/95 dark:from-back-darkprimery dark:to-back-darksecondary"
  };

  // Stats Section Data
  const statsData = [
    {
      icon: FaClock,
      value: "1+",
      label: "Years of Excellence",
      color: "text-color-1"
    },
    {
      icon: FaUsers,
      value: "50K+",
      label: "Happy Customers",
      color: "text-color-1"
    },
    {
      icon: FaAward,
      value: "100%",
      label: "100% Halal",
      color: "text-color-1"
    },
    {
      icon: PiChefHatLight,
      value: "20+",
      label: "Expert Chefs",
      color: "text-color-1"
    }
  ];

  // Story Section Data
  const storyData = {
    title: "Our Story",
    paragraphs: [
      "The story of Saba Restaurant for Mandi and Madbi began from a deep passion for bringing the authentic flavors of Yemen to Egypt. we have been offering our esteemed customers an exceptional experience in the world of traditional Yemeni cuisine.",
      "At Saba, we believe that food is not just a meal, but a cultural and heritage experience that reflects the rich history and civilization of Yemen. We use the finest fresh ingredients and authentic Yemeni spices to bring you the most delicious dishes.",
      "From aromatic Mandi to charcoal-grilled Madbi, from Mathbi to Haneeth, every dish is carefully prepared by our master chefs who master the secrets of Yemeni cuisine."
    ],
    features: [
      { text: "Fresh Ingredients", icon: FaCheckCircle },
      { text: "Traditional Cooking", icon: FaCheckCircle }
    ],
    image: aboutUsImage,
    quoteIcon: FaQuoteLeft
  };

  // Values Section Data
  const valuesData = {
    title: "Our Values",
    description: "We believe that quality and authenticity are the foundation of providing the best dining experience for our customers",
    items: [
      {
        icon: FaHeart,
        title: "Authenticity",
        description: "We preserve traditional Yemeni recipes without any changes",
        bgColor: "bg-red-50 dark:bg-red-900/20",
        iconColor: "text-red-500"
      },
      {
        icon: FaFire,
        title: "Quality",
        description: "We select the freshest ingredients to ensure the highest quality standards",
        bgColor: "bg-orange-50 dark:bg-orange-900/20",
        iconColor: "text-orange-500"
      },
      {
        icon: FaLeaf,
        title: "Tradition",
        description: "We take pride in preserving authentic Yemeni culinary heritage",
        bgColor: "bg-green-50 dark:bg-green-900/20",
        iconColor: "text-green-500"
      },
      {
        icon: GiHotSpices,
        title: "Flavors",
        description: "We use the finest Yemeni spices to bring out authentic flavors",
        bgColor: "bg-amber-50 dark:bg-amber-900/20",
        iconColor: "text-amber-600"
      }
    ]
  };

  // Specialties Section Data
  const specialtiesData = {
    title: "Our Specialties",
    description: "We offer a diverse selection of the most delicious traditional Yemeni dishes",
    items: [
      {
        icon: PiCowFill,
        name: "Mandi",
        description: "Aromatic Mandi rice with tender lamb cooked in a tandoor oven"
      },
      {
        icon: FaFire,
        name: "Madbi",
        description: "Charcoal-grilled meat with distinctive flavored rice"
      },
      {
        icon: GiChickenOven,
        name: "Chicken",
        description: "Chicken Mandi, Madbi, and Mathbi with the most delicious flavors"
      },
      {
        icon: FaFish ,
        name: "Seafood",
        description: "The freshest seafood dishes at today's market price"
      }
    ]
  };

  // Location & Contact Section Data
  const contactData = {
    title: "Visit Us",
    items: [
      {
        icon: FaMapMarkerAlt,
        title: "Address",
        value: "2 Hafez Ramadan, Nasr City"
      },
      {
        icon: FaPhoneAlt,
        title: "Phone",
        value: "01515888773"
      },
      {
        icon: FaClock,
        title: "Working Hours",
        value: "From 9 a.m. to 3 a.m."},
      {
        icon: MdDeliveryDining,
        title: "Hotline",
        value: "16152"
      }
    ],
    mapPlaceholder: "Location Map"
  };

  return (
    <>
      <Header active={2} DropActive={2} />
      <div className="bg-white dark:bg-back-darkprimery overflow-hidden">
        
        {/* Hero Section */}
        <div className={`relative bg-gradient-to-r ${heroData.bgGradient} py-20 md:py-28`}>
          <div className="absolute inset-0 bg-black/40 z-0"></div>
          <div className="container relative z-10 text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="flex justify-center mb-4">
                <heroData.icon className={`text-5xl ${heroData.iconColor}`} />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-kufi text-white mb-4">
                {t(heroData.title)}
              </h1>
              <div className="w-24 h-1 bg-color-1 mx-auto mb-6"></div>
              <p className="body-1 text-white/90 max-w-3xl mx-auto">
                {t(heroData.description)}
              </p>
            </motion.div>
          </div>
        </div>

        {/* Our Story Section */}
        <div className="container py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-kufi text-tcolor-4 dark:text-tcolor-1 mb-4">
                {t(storyData.title)}
              </h2>
              <div className="w-20 h-1 bg-color-1 mb-6"></div>
              {storyData.paragraphs.map((para, idx) => (
                <p key={idx} className="text-gray-600 dark:text-gray-300 body-1 mb-4">
                  {t(para)}
                </p>
              ))}
              
              <div className="mt-8 flex gap-6">
                {storyData.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <feature.icon className="text-color-1 text-xl" />
                    <span className="text-tcolor-4 body-3 dark:text-tcolor-1">{t(feature.text)}</span>
                  </div>
                ))}
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={storyData.image} 
                  alt="Restaurant" 
                  className="w-full h-[400px] object-cover"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-color-1 text-white p-4 rounded-xl shadow-lg">
                <storyData.quoteIcon className="text-3xl" />
              </div>
            </motion.div>
          </div>
        </div>

        {/* Stats Section */}
        <div className="bg-color-1/10 dark:bg-color-1/5 py-16">
          <div className="container">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {statsData.map((stat, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="text-center"
                >
                  <stat.icon className={`text-4xl md:text-5xl ${stat.color} mx-auto mb-3`} />
                  <div className="text-3xl md:text-4xl font-kufi text-tcolor-4 dark:text-tcolor-1">{stat.value}</div>
                  <div className="text-gray-500 body-3 mt-2 dark:text-gray-400">{t(stat.label)}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Our Values Section */}
        <div className="container py-20 md:py-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-kufi text-tcolor-4 dark:text-tcolor-1 mb-4">
              {t(valuesData.title)}
            </h2>
            <div className="w-20 h-1 bg-color-1 mx-auto mb-6"></div>
            <p className="text-gray-600 dark:text-gray-300 body-1 max-w-2xl mx-auto">
              {t(valuesData.description)}
            </p>
          </motion.div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {valuesData.items.map((value, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className={`${value.bgColor} rounded-2xl p-6 text-center hover:shadow-lg transition-shadow`}
              >
                <div className={`w-16 h-16 ${value.bgColor} rounded-full flex items-center justify-center mx-auto mb-4`}>
                  <value.icon className={`text-3xl ${value.iconColor}`} />
                </div>
                <h3 className="text-xl font-kufi text-tcolor-4 dark:text-tcolor-1 mb-2">
                  {t(value.title)}
                </h3>
                <p className="text-gray-500 dark:text-gray-400 body-2 ">
                  {t(value.description)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Our Specialties Section */}
        <div className="bg-gray-50 dark:bg-back-darksecondary py-20 md:py-28">
          <div className="container">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl md:text-4xl font-kufi text-tcolor-4 dark:text-tcolor-1 mb-4">
                {t(specialtiesData.title)}
              </h2>
              <div className="w-20 h-1 bg-color-1 mx-auto mb-6"></div>
              <p className="text-gray-600 dark:text-gray-300 body-1 max-w-2xl mx-auto">
                {t(specialtiesData.description)}
              </p>
            </motion.div>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {specialtiesData.items.map((specialty, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white dark:bg-back-darkprimery rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all"
                >
                  <div className="w-20 h-20 bg-color-1/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <specialty.icon className="text-4xl text-color-1" />
                  </div>
                  <h3 className="text-xl font-kufi text-tcolor-4 dark:text-tcolor-1 mb-2">
                    {t(specialty.name)}
                  </h3>
                  <p className="text-gray-500 dark:text-gray-400 body-2">
                    {t(specialty.description)}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Location & Contact Section */}
        <div className="container py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2 className="text-3xl md:text-4xl font-kufi text-tcolor-4 dark:text-tcolor-1 mb-4">
                {t(contactData.title)}
              </h2>
              <div className="w-20 h-1 bg-color-1 mb-6"></div>
              <div className="space-y-4">
                {contactData.items.map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <item.icon className="text-color-1  mt-1" />
                    <div>
                      <h3 className="h6 text-tcolor-4 dark:text-tcolor-1">{t(item.title)}</h3>
                      <p className="text-gray-500 body-2 dark:text-gray-400">
                        {t(item.value)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            
         
            <motion.div
              initial={{ opacity: 0, x: 60 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-back-darksecondary rounded-[2rem] p-6 shadow-2xl border border-color-1/10"
            >


<div
  className="
    relative
    h-full
    min-h-[400px]
    overflow-hidden
    rounded-[1.5rem]
    border
    border-color-1/20
    bg-back-secondary
    dark:bg-back-darktertiary
  "
>
<iframe
  src="https://www.google.com/maps?q=مطعم+سبأ+للمندي+والمظبي،+2+حافظ+رمضان+تقاطع+محمد+عوض،+أول+مكرم+عبيد،+مدينة+نصر،+القاهرة&z=17&hl=ar&output=embed"
  title={t("sabaMapTitle")}
  className="
    absolute
    inset-0
    w-full
    h-full
    border-0
  "
  loading="lazy"
  allowFullScreen
  referrerPolicy="no-referrer-when-downgrade"
/>

  {/* Location Badge */}
 
</div>



            </motion.div>
          </div>
        </div>

      </div>
      <Fotter />
    </>
  );
}

export default AboutUs;